import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { postsApi, listsApi } from '../services/api';
import PostCard from '../components/PostCard/PostCard';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import { useAuth } from '../context/AuthContext';
import { useLists } from '../context/ListsContext';
import './Feed.css';
import { APP_OPTIONS } from '../config/appOptions';

const SORT_OPTIONS = [
  { value: 'created',       label: 'Recent' },
  { value: 'trending',      label: 'Trending' },
  { value: 'following',     label: 'Following',     requiresAuth: true },
  { value: 'subscriptions', label: 'Subscriptions', requiresAuth: true },
];


const ArticlesFeed = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user } = useAuth();
  const { lists } = useLists();
  const { app: appParam, sort: sortParam } = useParams();
  const navigate = useNavigate();
  const activeContentListMatches = (lists || []).filter(l => l.name === sortParam);
  const activeList = activeContentListMatches.length > 0 ? activeContentListMatches[0] : null;
  const isListFeed = activeList !== null;
  
  const app = APP_OPTIONS.some(o => o.value === appParam) ? appParam : 'flexus';
  const sort = SORT_OPTIONS.some(o => o.value === sortParam) || isListFeed ? sortParam : 'created';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [showListsDropdown, setShowListsDropdown] = useState(false);
  const [listsDropdownPos, setListsDropdownPos] = useState({ top: 0, left: 0 });
  const listsDropdownRef = React.useRef(null);
  const listsDropdownPortalRef = React.useRef(null);
  const listsBtnRef = React.useRef(null);

  const listsWithMembers = (lists || []).filter(l => l.members && l.members.length > 0);

  const LIMIT = 20;

  // Click outside for lists dropdown
  useEffect(() => {
    if (!showListsDropdown) return;
    const handleClickOutside = (e) => {
      if (
        listsDropdownRef.current && !listsDropdownRef.current.contains(e.target) &&
        (!listsDropdownPortalRef.current || !listsDropdownPortalRef.current.contains(e.target))
      ) {
        setShowListsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showListsDropdown]);

  const fetchPosts = useCallback(async (currentSort, currentApp) => {
    setLoading(true);
    setError('');
    setOffset(0);
    try {
      let data;
      if (isListFeed) {
        data = await listsApi.getListArticlesFeed(activeList.id, LIMIT, 0, 'created', currentApp);
      } else {
        const observer = (currentSort === 'following' || currentSort === 'subscriptions') ? user?.username : null;
        data = await postsApi.getArticlesFeed(LIMIT, 0, currentSort, currentApp, observer);
      }
      setPosts(data.posts || []);
      setHasMore(data.hasMore || false);
      setOffset(LIMIT);
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.username, isListFeed, activeList?.id]);

  useEffect(() => {
    if ((sort === 'following' || sort === 'subscriptions') && !user) {
      navigate(`/articles/${app}/created`, { replace: true });
      return;
    }
    fetchPosts(sort, app);
  }, [fetchPosts, sort, app, user, navigate]);

  const handleSortChange = (newSort) => {
    if ((newSort === 'following' || newSort === 'subscriptions') && !user) return;
    const isTargetList = lists.some(l => l.name === newSort);
    // Following, Subscriptions and Lists always force app to 'all' on click
    const targetApp = (newSort === 'following' || newSort === 'subscriptions' || isTargetList) ? 'all' : app;
    navigate(`/articles/${targetApp}/${encodeURIComponent(newSort)}`);
  };

  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      let data;
      if (isListFeed) {
        data = await listsApi.getListArticlesFeed(activeList.id, LIMIT, offset, 'created', app);
      } else {
        const observer = (sort === 'following' || sort === 'subscriptions') ? user?.username : null;
        data = await postsApi.getArticlesFeed(LIMIT, offset, sort, app, observer);
      }
      const newPosts = data.posts || [];
      setPosts(prev => [...prev, ...newPosts]);
      setHasMore(data.hasMore || false);
      setOffset(prev => prev + LIMIT);
    } catch (err) {
      console.error('Error loading more articles:', err);
      setError('Failed to load more articles.');
    } finally {
      setLoadingMore(false);
    }
  };

  const stickyControls = (
    <div className="community-stick">
      <div className="community-header-bar">
        <button
          className="menu-open-btn left-toggle"
          onClick={onOpenLeftSidebar}
          aria-label="Open left menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <div className="community-header-bar-info">
          <h2 className="community-header-bar-title">Articles</h2>
        </div>
        <div className="community-header-bar-actions">
          <button
            className="menu-open-btn right-toggle"
            onClick={onOpenRightSidebar}
            aria-label="Open user menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
          </button>
        </div>
      </div>
      <div className="community-controls-bar">
        {/* Sort Tabs & Lists Menu */}
        <div className="feed-tabs-container">
          <div className="feed-tabs desktop-only">
            {SORT_OPTIONS.map(opt => {
              const disabled = opt.requiresAuth && !user;
              return (
                <button
                  key={opt.value}
                  className={`feed-tab ${sort === opt.value ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
                  onClick={() => !disabled && handleSortChange(opt.value)}
                  title={disabled ? 'Login to use Following' : undefined}
                >
                  {opt.label}
                </button>
              );
            })}
            {user && listsWithMembers.length > 0 && (
              <div className="feed-tab-lists-wrapper" ref={listsDropdownRef}>
                <button
                  ref={listsBtnRef}
                  className={`feed-tab feed-tab-lists${isListFeed ? ' active' : ''}`}
                  onClick={() => {
                    const rect = listsBtnRef.current?.getBoundingClientRect();
                    if (rect) setListsDropdownPos({ top: rect.bottom + 2, left: rect.left });
                    setShowListsDropdown(v => !v);
                  }}
                  aria-haspopup="listbox"
                  aria-expanded={showListsDropdown}
                >
                  {isListFeed ? activeList.name : 'Lists'} ▾
                </button>
                {showListsDropdown && ReactDOM.createPortal(
                  <div
                    ref={listsDropdownPortalRef}
                    className="feed-tab-lists-dropdown"
                    role="listbox"
                    style={{ top: listsDropdownPos.top, left: listsDropdownPos.left }}
                  >
                    {listsWithMembers.map(list => (
                      <button
                        key={`menu-list-${list.id}`}
                        className="feed-tab-lists-item"
                        role="option"
                        aria-selected={false}
                        onClick={() => {
                          setShowListsDropdown(false);
                          handleSortChange(list.name);
                        }}
                      >
                        <span className="lists-item-name">{list.name}</span>
                        <span className="lists-item-count">{list.members?.length || 0}</span>
                      </button>
                    ))}
                  </div>,
                  document.body
                )}
              </div>
            )}
          </div>
          <select
            className="community-sort-dropdown mobile-only"
            value={isListFeed ? encodeURIComponent(sort) : sort}
            onChange={e => handleSortChange(decodeURIComponent(e.target.value))}
          >
            {SORT_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value} disabled={opt.requiresAuth && !user}>
                {opt.label}{opt.requiresAuth && !user ? ' (login required)' : ''}
              </option>
            ))}
            {listsWithMembers.length > 0 && (
              <optgroup label="Lists">
                {listsWithMembers.map(list => (
                  <option key={`mob-list-${list.id}`} value={encodeURIComponent(list.name)}>
                    {list.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* App Filter Dropdown */}
        <select
          className="articles-app-dropdown"
          value={app}
          onChange={e => {
            const newApp = e.target.value;
            navigate(`/articles/${newApp}/${encodeURIComponent(sort)}`);
          }}
        >
          {APP_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <div className="feed-container">
      {stickyControls}

      {loading && <LoadingSpinner text="Loading articles..." />}

      {error && (
        <div className="error-box">
          <p>{error}</p>
          <button onClick={() => fetchPosts(sort, app)} className="retry-btn">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && posts.length === 0 && (
        <div className="empty-state">
          <p>No articles found.</p>
        </div>
      )}

      <div className="posts-list" style={{ paddingTop: '8px' }}>
        {posts.map((post, index) => (
          <PostCard
            key={`${post.author}-${post.permlink}-${index}`}
            post={post}
            showActions={true}
          />
        ))}
      </div>

      {!loading && posts.length > 0 && hasMore && (
        <div className="load-more-container">
          <button
            className="load-more-btn"
            onClick={loadMorePosts}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading...' : `Load More (${posts.length} loaded)`}
          </button>
        </div>
      )}

      {!loading && posts.length > 0 && !hasMore && (
        <div className="end-message">
          <p>No more articles to load</p>
        </div>
      )}
    </div>
  );
};

export default ArticlesFeed;
